import { Button, InlineNotification, Tooltip } from '@carbon/react';
import { Information } from '@carbon/react/icons';
import { useParams } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { useState } from 'react';

import styles from './config-form-header.module.scss';
import { useScenarioForm } from './contexts';
import { AreaConfigResponse } from './types';
import { buildConfigDisplayName, hasTypeWellConfigErrors } from './utils';
import { typeCurveScenarioDataAtom } from './atoms';
import { SuccessToast } from 'components/success-toast';
import ErrorToast from 'components/error-toast';

interface ConfigFormHeaderProps {
  isScenarioDetailsSelected: boolean;
}

export const ConfigFormHeader = ({
  isScenarioDetailsSelected = false,
}: ConfigFormHeaderProps) => {
  const { id: scenarioId } = useParams<{ id: string }>();
  const {
    form,
    isFormModified,
    handleSubmitForm,
    selectedAreaConfig,
    readOnly,
    validConfigState,
    isValidating,
  } = useScenarioForm();
  const scenarioData = useAtomValue(typeCurveScenarioDataAtom);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);

  return (
    <>
      {showSuccessToast && (
        <SuccessToast
          message="Scenario saved"
          subtitle="Your scenario has been saved successfully."
          onClose={() => {
            setShowSuccessToast(false);
            return true;
          }}
        />
      )}
      {showErrorToast && scenarioData.error && (
        <ErrorToast
          message={scenarioData.error || 'sdsds'}
          onClose={() => {
            setShowErrorToast(false);
            return true;
          }}
        />
      )}

      <div className={styles.configFormHeader}>
        <div className={styles.configFormHeaderTitle}>
          {isScenarioDetailsSelected && <GlobalConfigsFormTitle />}
          {!isScenarioDetailsSelected && (
            <AreaConfigsFormTitle selectedAreaConfig={selectedAreaConfig} />
          )}
        </div>

        <div className={styles.configFormActionsContainer}>
          {!readOnly && (
            <form.Subscribe
              selector={(state) => {
                const isModified = isFormModified(state.values);
                const { globalConfig, areaConfig } = state.values;
                const hasValidScenarioDetails =
                  !!globalConfig.name?.trim() && !!globalConfig.dcaScenarioId;
                const requiresBasinName =
                  areaConfig?.basinAreaCategory === 'structure_grid_collection';
                const hasValidAreaConfig = requiresBasinName
                  ? !!areaConfig?.areaGeometryId &&
                    !!areaConfig?.basinName?.trim()
                  : !!areaConfig?.areaGeometryId;
                const canSubmit = isScenarioDetailsSelected
                  ? hasValidScenarioDetails
                  : !hasTypeWellConfigErrors(state.values.typeWellConfigs);

                return { isModified, canSubmit, hasValidAreaConfig };
              }}
            >
              {({ isModified, canSubmit, hasValidAreaConfig }) => (
                <>
                  {isModified && (
                    <InlineNotification
                      lowContrast
                      kind="info"
                      title="Unsaved changes"
                      hideCloseButton={true}
                    />
                  )}
                  <Button
                    size="md"
                    kind="primary"
                    disabled={
                      scenarioData.loading ||
                      !isModified ||
                      !canSubmit ||
                      (!isScenarioDetailsSelected && !hasValidAreaConfig) ||
                      (!isScenarioDetailsSelected && isValidating) ||
                      (!isScenarioDetailsSelected &&
                        validConfigState !== null &&
                        !validConfigState.isValid)
                    }
                    onClick={() => {
                      handleSubmitForm({
                        typeCurveScenarioId: scenarioId || null,
                        areaConfigId:
                          !isScenarioDetailsSelected && selectedAreaConfig?.href
                            ? selectedAreaConfig.id
                            : null,
                        onSuccess: () => setShowSuccessToast(true),
                        onError: () => setShowErrorToast(true),
                      });
                    }}
                  >
                    {scenarioData.loading
                      ? 'Saving...'
                      : 'Save Type Curve Scenario'}
                  </Button>
                </>
              )}
            </form.Subscribe>
          )}
        </div>
      </div>
    </>
  );
};

const GlobalConfigsFormTitle = () => {
  return (
    <>
      <h2 className={styles.configFormHeaderTitleText}>Scenario Details</h2>
      <Tooltip
        align="right"
        label="Scenario Details defined on this form apply to all areas."
      >
        <Information className={styles.configFormInfoIcon} size={16} />
      </Tooltip>
    </>
  );
};

const AreaConfigsFormTitle = ({
  selectedAreaConfig,
}: {
  selectedAreaConfig: AreaConfigResponse | null;
}) => {
  const isSaved = selectedAreaConfig?.href;
  if (!isSaved) {
    return <h2 className={styles.configFormHeaderTitleText}>New Config</h2>;
  }

  const title = buildConfigDisplayName(selectedAreaConfig, 'Area Config');

  return <h2 className={styles.configFormHeaderTitleText}>{title}</h2>;
};
